import {z} from 'zod';
import {extensionKeySchema} from '../shared';

export const targetValueSchema = z.unknown();

const targetTupleSimple = z.tuple([
  /** Extension key for target type */
  extensionKeySchema,
  /** Target value (plugin-defined) */
  targetValueSchema,
]);

/**
 * Core filtering relationships between targets
 * Part of core targeting system, enables relationship-based element selection
 */
export const relationshipTypeSchema = z.enum([
  'none',
  'within',
  'direct-child-of',
  'contains',
  'direct-parent-of',
  'next-to',
  'next-sibling-of',
  'prev-sibling-of',
]);
export type RelationshipType = z.infer<typeof relationshipTypeSchema>;

/**
 * Core filtering context for target definitions
 * Enables relationship-based targeting as a fundamental feature
 */
export const filterContextSchema = z.object({
  /** Relationship type between primary and filter targets */
  relationship: relationshipTypeSchema,
  /** Simple target selector for filtering (2-tuple to prevent nesting) */
  filterBy: z.tuple([extensionKeySchema, z.unknown()]),
  /** Performance optimization for single-result relationships */
  firstMatchOnly: z.boolean().default(false),
});
export type FilterContext = z.infer<typeof filterContextSchema>;

/**
 * Target definition with optional filtering
 * Follows the same pattern as Condition and Trigger tuples
 */
export const targetTupleSchema = z.union([
  // Simple targeting (90%+ of cases)
  targetTupleSimple,
  // With filtering (new core feature)
  z.tuple([
    /** Extension key for target type */
    extensionKeySchema,
    /** Target value (plugin-defined) */
    targetValueSchema,
    /** Optional filtering context */
    filterContextSchema,
  ]),
]);

export type TargetTupleSimple<T = unknown> = [
  z.infer<typeof extensionKeySchema>,
  T,
];

export type TargetTuple<T = unknown> =
  | TargetTupleSimple<T>
  | [z.infer<typeof extensionKeySchema>, T, FilterContext];

/**
 * FilteredTargetDefinition is always 3-tuple (used in new filtering UI)
 * Regular TargetDefinition can be 2 or 3-tuple (backward compatible)
 */
export type FilteredTargetDefinition<T = unknown> = [
  z.infer<typeof extensionKeySchema>,
  T,
  FilterContext,
];

export type TargetDefinition<T = unknown> = TargetTuple<T>;

export const targetObjectSchema = z.object({
  /** Extension key for the target e.g. "wf:class" */
  extensionKey: extensionKeySchema,
  /** Value for the target e.g. "my-class" or ["class1", "class2"] */
  value: targetValueSchema,
  /** Optional filtering context */
  filterContext: filterContextSchema.optional(),
});
export type TargetObject<T = unknown> = {
  extensionKey: z.infer<typeof extensionKeySchema>;
  value: T;
  filterContext?: FilterContext;
};

/**
 * Native ID path representing an element within a component instance.
 * Format: [componentId, elementId]
 *
 * Used by wf:inst targets to identify specific elements.
 */
export type NativeIdPath = [componentId: string, elementId: string];

export const targetTransforms = {
  tupleToObject: <T = unknown>(tuple: TargetTuple<T>): TargetObject<T> => {
    const [extensionKey, value, filterContext] = tuple;
    return filterContext
      ? {extensionKey, value, filterContext}
      : {extensionKey, value};
  },

  objectToTuple: <T = unknown>(obj: TargetObject<T>): TargetTuple<T> => {
    return obj.filterContext
      ? [obj.extensionKey, obj.value, obj.filterContext]
      : [obj.extensionKey, obj.value];
  },

  hasFilter: (
    target: TargetDefinition
  ): target is [z.infer<typeof extensionKeySchema>, unknown, FilterContext] => {
    return target.length === 3;
  },

  getFilter: (target: TargetDefinition): FilterContext | undefined => {
    return target.length === 3 ? target[2] : undefined;
  },
};
